import { ArrowLeftIcon, BuildingOffice2Icon, CheckCircleIcon, PaperAirplaneIcon, UserIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/Ui/Button';
import { AdaptiveSelect } from '@/components/Ui/Form/Rhf/AdaptiveSelect';
import { FieldWithLabel } from '@/components/Ui/Form/Rhf/FieldWithLabel';
import { LocaleSelect } from '@/components/Ui/Form/Rhf/LocaleSelect';
import { TelephoneInput } from '@/components/Ui/Form/Rhf/TelephoneInput';
import { useCountries } from '@/hooks/useCountries';
import { isFormTokenError, type RegistrationErrorBody, useRegister, useRegistrationForm } from '@/hooks/useRegistration';

import { registerDefaults, registerSchema, type RegisterValues, toApiPayload } from './RegisterPage.schema';

interface SectionProps {
    children: ReactNode;
    icon: ReactNode;
    index: number;
    title: string;
}

/**
 * A numbered step header (icon badge, title, hairline rule) followed by a
 * six-column field grid. Each field is `inline` (label above input) so the
 * grid alone dictates alignment — no side-by-side label/input splits.
 */
function Section({ children, icon, index, title }: SectionProps) {
    const id = `register-section-${ index }`;

    return (
        <section aria-labelledby={ id }>
            <div className='flex items-center gap-3'>
                <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20'>
                    { icon }
                </span>
                <div className='flex items-baseline gap-2'>
                    <span className='text-xs font-semibold tabular-nums tracking-widest text-primary'>
                        { String(index).padStart(2, '0') }
                    </span>
                    <h2 className='text-sm font-semibold uppercase tracking-wide text-foreground' id={ id }>
                        { title }
                    </h2>
                </div>
                <span aria-hidden='true' className='h-px flex-1 bg-border' />
            </div>
            <div className='mt-5 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-6'>
                { children }
            </div>
        </section>
    );
}

const RegisterPage = function () {
    const [
        t,
        i18n,
    ] = useTranslation('pages');

    const [
        submitted,
        setSubmitted,
    ] = useState(false);
    const [
        retrying,
        setRetrying,
    ] = useState(false);

    const { countries } = useCountries();
    const countrySelectOptions = countries.map((c) => {
        return { value: c.code, label: c.label };
    });

    const { formToken, refetch: refetchFormToken } = useRegistrationForm(!submitted);
    const register = useRegister();

    const form = useForm<RegisterValues>({
        resolver: zodResolver(registerSchema),
        defaultValues: registerDefaults(i18n.language?.substring(0, 2) || 'en'),
        mode: 'onBlur',
    });
    const { control, handleSubmit, setError, formState: { isSubmitting } } = form;
    const countryCode = useWatch({ control, name: 'country_code' });

    const applyServerErrors = (error: unknown): boolean => {
        const body = (error as { response?: { data?: RegistrationErrorBody } })?.response?.data;
        const errors = body?.errors ?? {};
        let applied = false;

        Object.entries(errors).forEach(([
            field,
            messages,
        ]) => {
            if (field in registerDefaults() && field !== 'website') {
                setError(field as keyof RegisterValues, { type: 'server', message: messages[0] });
                applied = true;
            }
        });

        return applied;
    };

    const reportError = (error: unknown) => {
        if (!applyServerErrors(error)) {
            toast.error(t('Something went wrong, please try again.'));
        }
    };

    const attempt = async (values: RegisterValues, token: string) => {
        await register.mutateAsync(toApiPayload(values, token));
        setSubmitted(true);
    };

    const submit = async (values: RegisterValues) => {
        const current = formToken ?? await refetchFormToken();

        if (!current) {
            toast.error(t('Something went wrong, please try again.'));

            return;
        }

        try {
            await attempt(values, current.token);

            return;
        } catch (error) {
            if (!isFormTokenError(error)) {
                reportError(error);

                return;
            }
        }

        // A stale (tab left open) or too-fresh (autofilled) form token is
        // not the visitor's fault: fetch a new one and retry once. The
        // backend refuses tokens younger than `minAgeSeconds`, so the retry
        // has to wait that long before it can succeed.
        const fresh = await refetchFormToken();

        if (!fresh) {
            toast.error(t('Something went wrong, please try again.'));

            return;
        }

        setRetrying(true);

        try {
            await new Promise((resolve) => {
                setTimeout(resolve, (fresh.minAgeSeconds + 1) * 1000);
            });
            await attempt(values, fresh.token);
        } catch (error) {
            reportError(error);
        } finally {
            setRetrying(false);
        }
    };

    if (submitted) {
        return (
            <div className='w-full max-w-2xl text-foreground'>
                <div className='flex items-center gap-3'>
                    <CheckCircleIcon className='h-8 w-8 text-primary shrink-0' />
                    <h1 className='text-2xl font-bold tracking-tight text-primary'>{ t('Thank you for your registration') }</h1>
                </div>
                <p className='mt-4'>{ t('We have received your request and sent you a confirmation by e-mail.') }</p>

                <p className='font-bold my-4'>{ t('What happens next:') }</p>
                <div className='bg-muted border-border border p-4 my-4 rounded-lg text-sm'>
                    <ul className='list-inside list-decimal'>
                        <li className='mb-2'>{ t('Our team reviews your request.') }</li>
                        <li className='mb-2'>{ t('We contact you to discuss the terms of our collaboration.') }</li>
                        <li>{ t('Once approved, you receive an e-mail with a link to set your password.') }</li>
                    </ul>
                </div>

                <NavLink
                    className='group relative flex gap-2 w-full text-sm justify-center rounded-md border border-transparent bg-transparent hover:text-primary mt-8'
                    to='/login'
                >
                    <ArrowLeftIcon className='w-5 h-5' />
                    { t('Back to login') }
                </NavLink>
            </div>
        );
    }

    return (
        <div className='w-full max-w-2xl text-foreground'>
            <header>
                <h1 className='text-2xl font-bold tracking-tight text-primary'>{ t('Register your company') }</h1>
                <p className='mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground'>
                    { t('Interested in selling Félice Headwear? Tell us about your company and we will get in touch to set up your account.') }
                </p>
            </header>

            <FormProvider { ...form }>
                <form className='mt-8 space-y-10' noValidate onSubmit={ handleSubmit(submit) }>
                    <Section icon={ <BuildingOffice2Icon className='h-5 w-5' /> } index={ 1 } title={ t('Your company') }>
                        <div className='sm:col-span-4'>
                            <FieldWithLabel
                                elementProps={ { autoComplete: 'organization' } }
                                inline
                                name='company_name'
                                required
                                verbose
                            />
                        </div>
                        <div className='sm:col-span-2'>
                            <FieldWithLabel inline name='vat_number' verbose />
                        </div>
                        <div className='sm:col-span-6'>
                            <FieldWithLabel
                                elementProps={ { autoComplete: 'street-address' } }
                                inline
                                name='street'
                                required
                                verbose
                            />
                        </div>
                        <div className='sm:col-span-2'>
                            <FieldWithLabel
                                elementProps={ { autoComplete: 'postal-code' } }
                                inline
                                name='postal_code'
                                required
                                verbose
                            />
                        </div>
                        <div className='sm:col-span-4'>
                            <FieldWithLabel
                                elementProps={ { autoComplete: 'address-level2' } }
                                inline
                                name='city'
                                required
                                verbose
                            />
                        </div>
                        <div className='sm:col-span-3'>
                            <FieldWithLabel
                                Element={ AdaptiveSelect }
                                elementProps={ { options: countrySelectOptions, placeholder: t('Select a country') } }
                                inline
                                name='country_code'
                                required
                                verbose
                            />
                        </div>
                        <div className='sm:col-span-3'>
                            <FieldWithLabel
                                Element={ TelephoneInput }
                                elementProps={ {
                                    defaultPrefix: countries.find((c) => {
                                        return c.code === countryCode;
                                    })?.dial_code ?? '+34',
                                } }
                                inline
                                name='telephone'
                                verbose
                            />
                        </div>
                    </Section>

                    <Section icon={ <UserIcon className='h-5 w-5' /> } index={ 2 } title={ t('Contact person') }>
                        <div className='sm:col-span-3'>
                            <FieldWithLabel
                                elementProps={ { autoComplete: 'given-name' } }
                                inline
                                name='first_name'
                                required
                                verbose
                            />
                        </div>
                        <div className='sm:col-span-3'>
                            <FieldWithLabel
                                elementProps={ { autoComplete: 'family-name' } }
                                inline
                                name='last_name'
                                required
                                verbose
                            />
                        </div>
                        <div className='sm:col-span-4'>
                            <FieldWithLabel
                                elementProps={ { autoComplete: 'email', type: 'email' } }
                                inline
                                name='email'
                                required
                                verbose
                            />
                        </div>
                        <div className='sm:col-span-2'>
                            <FieldWithLabel
                                Element={ LocaleSelect }
                                inline
                                name='locale'
                                required
                                verbose
                            />
                        </div>
                    </Section>

                    {/* Honeypot: off-screen and skipped by keyboard navigation; bots fill it, people never see it. */}
                    <div aria-hidden='true' className='absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden'>
                        <label htmlFor='website'>Website</label>
                        <input
                            autoComplete='off'
                            id='website'
                            tabIndex={ -1 }
                            type='text'
                            { ...form.register('website') }
                        />
                    </div>

                    <footer className='space-y-4 border-t border-border pt-6'>
                        <p className='text-xs leading-relaxed text-muted-foreground'>
                            { t('By submitting this form you agree that we store the details above to process your request.') }
                        </p>

                        <Button className='h-12 w-full text-base' disabled={ isSubmitting || register.isPending } type='submit'>
                            <PaperAirplaneIcon aria-hidden='true' className='h-5 w-5' />
                            { t('Request access') }
                        </Button>
                        { retrying && (
                            <p aria-live='polite' className='text-center text-sm text-muted-foreground'>
                                { t('Verifying your submission, one moment...') }
                            </p>
                        ) }

                        <NavLink
                            className='flex w-full items-center justify-center gap-2 pt-2 text-sm text-muted-foreground transition-colors hover:text-primary'
                            to='/login'
                        >
                            <ArrowLeftIcon className='h-4 w-4' />
                            { t('Back to login') }
                        </NavLink>
                    </footer>
                </form>
            </FormProvider>
        </div>
    );
};

export default RegisterPage;
